<template>
  <Container>
    <ul
      class="grid w-full grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 xl:grid-cols-4"
    >
      <li
        v-for="article in facebookFeedSliced"
        :key="article.id"
        :class="article.colSpan"
      >
        <NewsCard :data="article" />
      </li>
    </ul>

    <!-- LOAD MORE -->
    <div
      v-show="
        maxAmountPosts > filter.limit || !(filter.limit > facebookFeed?.length)
      "
      class="mt-14 flex w-full justify-center"
    >
      <AppButton @click="filter.limit += initialLimit">
        {{ $t('filter.load_more') }}
      </AppButton>
    </div>

    <!-- NO ENTRIES FOUND -->
    <transition name="fade">
      <div
        v-show="facebookFeed?.length === 0"
        class="my-10 flex w-full items-center justify-center gap-4 text-blue-dark-800 sm:my-20"
      >
        <MagnifyingLensIcon class="w-[1.125rem] shrink-0" />
        <p class="text-md text-blue-dark-800">
          {{ $t('filter.no_entries_found') }}
        </p>
      </div>
    </transition>
  </Container>
</template>

<script setup>
const props = defineProps({
  id: { type: String, required: true },
})

const config = useRuntimeConfig()
const route = useRoute()
const params = useUrlSearchParams('history')
const facebookFeed = ref(null)
const maxAmountPosts = ref(64)
const initialLimit = ref(16)

// Populate the filter
let routeQueryFilter = route.query[`newshub-facebook-${props.id}`]
if (routeQueryFilter) {
  routeQueryFilter = JSON.parse(routeQueryFilter)
}

const filter = ref(
  routeQueryFilter || {
    limit: initialLimit.value,
  }
)

// WATCHERS
watch(
  filter,
  (newValue) => {
    params[`newshub-facebook-${props.id}`] = JSON.stringify(newValue)
    fetchFacebookFeed()
  },
  { deep: true }
)

// COMPUTED
const facebookFeedSliced = computed(() => {
  if (!Array.isArray(facebookFeed.value) || !facebookFeed.value.length) {
    return []
  }
  return facebookFeed.value.slice(0, filter.value.limit)
})

// FUNCTIONS
// Map the facebook feed date to the same format as the news
function mapFacebookFeedPosts(feed) {
  return feed
    .filter((post) => post.message) // Only consider posts with a message property
    .map((post) => {
      const dateObj = new Date(post.created_time)

      return {
        id: post.id,
        slug: post.id,
        isFacebookPost: true,
        heading: null,
        title: 'Facebook post',
        date: dateObj
          .toLocaleDateString('de', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          })
          .split('.')
          .reverse()
          .join('-'),
        lead: processFeedText(post.message),
        url: getUrl(post.message),
        image_or_video: [],
        news_type: {
          title: 'News',
          slug: 'news',
        },
      }
    })
}

function processFeedText(text) {
  // Remove URLs
  return text.replaceAll(/https?:\/\/[^\s]+/g, '').trim()
}

function getUrl(text) {
  // Extract the first URL, optionally starting with a newline
  const matched = text.match(/https?:\/\/[^\s]+/)

  // If a match is found, return it. Otherwise, return null.
  return matched ? matched[0].trim() : null
}

async function fetchFacebookFeed() {
  if (filter.value.limit > maxAmountPosts.value) {
    return
  }
  try {
    const response = await fetch(
      `https://graph.facebook.com/${config.public.FB_PAGE_ID}/feed?access_token=${config.public.FB_APP_ACCESS_TOKEN}&limit=${filter.value.limit}`
    )
    // Extract JSON data
    const data = await response.json()
    facebookFeed.value = mapFacebookFeedPosts(data.data)
  } catch (error) {
    console.error(error)
  }
}

// INITIAL (lazy)
fetchFacebookFeed()
</script>
